import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserMenu } from '@/components/common/UserMenu';
import { ClientesList, ChatWindow, ClienteProfile, NovaConversaDialog } from '@/components/painel';
import { useFeatureTracking } from '@/hooks/useFeatureTracking';
import { useOrganization } from '@/hooks/useOrganization';
import { useWhatsAppChannel } from '@/hooks/useWhatsAppChannel';

export default function Painel() {
  useFeatureTracking('painel');
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clienteSelecionadoId, setClienteSelecionadoId] = useState<string | undefined>();
  const [novaConversaAberta, setNovaConversaAberta] = useState(false);
  const { organizationId } = useOrganization();
  const { data: whatsappChannel } = useWhatsAppChannel(organizationId ?? undefined);
  const whatsappConnected = whatsappChannel?.status === 'connected';

  useEffect(() => {
    const clienteId = searchParams.get('cliente');
    if (clienteId) {
      setClienteSelecionadoId(clienteId);
    }
  }, [searchParams]);

  const handleSelectCliente = (id: string) => {
    setClienteSelecionadoId(id);
    setSearchParams({ cliente: id });
  };

  const handleCloseChat = () => {
    setClienteSelecionadoId(undefined);
    setSearchParams({});
  };

  return (
    <MainLayout>
      <div className="h-[calc(100vh-1px)] p-4 flex flex-col gap-4 overflow-hidden">
        <PageHeader
          titulo={t('panel.title', 'Chat')}
          acoes={
            <>
              <button
                type="button"
                className="ow-btn ow-btn--primary ow-btn--sm"
                onClick={() => setNovaConversaAberta(true)}
                disabled={!whatsappConnected}
                title={!whatsappConnected ? t('panel.newConversationDialog.noChannel') : undefined}
              >
                <Plus size={14} />
                {t('panel.newConversation', 'Nova Conversa')}
              </button>
              <UserMenu />
            </>
          }
        />

        <NovaConversaDialog open={novaConversaAberta} onOpenChange={setNovaConversaAberta} />

        <div className="painel flex-1 min-h-0">
          <aside className="painel__list">
            <ClientesList
              clienteSelecionadoId={clienteSelecionadoId}
              onSelectCliente={handleSelectCliente}
            />
          </aside>

          <main className="painel__main">
            <ChatWindow clienteId={clienteSelecionadoId} />
          </main>

          <aside className="painel__profile">
            <ClienteProfile clienteId={clienteSelecionadoId} onClose={handleCloseChat} />
          </aside>
        </div>
      </div>
    </MainLayout>
  );
}
